import React, { FC, useEffect } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography, Box, useMediaQuery, useTheme } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { SignInForm, SignInFormValues } from 'src/components/Forms/SignInForm';
import { signInSchema } from 'src/components/Forms/validationSchemas';
import { Link } from 'src/components/Link/styled';
import { Modal } from 'src/components/Modals';
import { Footer } from 'src/components/Footer';
import { Logo } from 'src/components/Logo';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { SIGN_IN } from 'src/utils/gql/queries';
import { Query, QuerySignInArgs } from 'src/utils/gql/types';
import { setToken, getToken } from 'src/utils/localStorage';
import { useLocation } from 'react-router-dom';

export const SignIn: FC = () => {
  const snackbar = useSnackbar();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  // Gestisci parametri Shopify e redirect utenti già autenticati
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    const shop = searchParams.get('shop');

    // 1) Se c'è un code Shopify, prosegui con il flusso OAuth (aggiunta connettore)
    if (code && shop) {
      const shopifyCallbackUrl = `/${ROUTE_SOURCES.SHOPIFY_AUTH_CALLBACK}?${searchParams.toString()}`;
      navigate(shopifyCallbackUrl, { replace: true });
      return;
    }

    // 2) Se l'utente è già autenticato, portalo in app
    const token = getToken();
    if (token) {
      navigate(`/${ROUTE_SOURCES.CONVERSATION}`, { replace: true });
    }
  }, [location.search, navigate]);

  const formMethods = useForm<SignInFormValues>({
    mode: 'onChange',
    resolver: yupResolver(signInSchema),
    criteriaMode: 'all',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [login, { loading }] = useMutation<
    Pick<Query, 'signIn'>,
    QuerySignInArgs
  >(SIGN_IN, {
    onCompleted: ({ signIn }) => {
      // Check if response is JSON with expired trial information
      try {
        const response = JSON.parse(signIn);
        if (response.trialExpired) {
          // Save token and redirect to settings page
          setToken(response.token);
          navigate(
            response.redirectTo || '/settings?tab=pricing&trial=expired',
          );
          return;
        }
      } catch (e) {
        // Se non è JSON, è un token normale
      }

      // Token normale - navigazione standard
      setToken(signIn);
      navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
    },
    onError: (error) => {
      const { graphQLErrors, message: errorText } = error as ApolloError;
      const graphqlError =
        graphQLErrors && graphQLErrors.length > 0
          ? graphQLErrors[0]
          : undefined;
      const message = graphqlError?.message ?? errorText;
      const code = graphqlError?.extensions?.code;

      if (code === 'USER_NOT_ACTIVATED') {
        snackbar(message, 'info');
      } else if (error) {
        snackbar(message);
      }
    },
  });

  const handleSubmit: SubmitHandler<SignInFormValues> = (signInData) =>
    login({ variables: { signInData } });

  // Persisti shop se presente nell'URL per uso dopo il login
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const shop = params.get('shop');
      if (shop) {
        sessionStorage.setItem('shopify_shop', shop);
      }
    } catch {}
  }, [location.search]);

  return (
    <Modal open title={<Logo variant="default" size="large" />} hideBackdrop>
      <>
        <Typography
          variant="h2"
          textAlign="center"
          mb={isSmallScreen ? '25px' : '40px'}
        >
          Sign In
        </Typography>
        <FormProvider {...formMethods}>
          <SignInForm onSubmit={handleSubmit} loading={loading} />
        </FormProvider>
        <Typography textAlign="center" variant="body2" mt="20px">
          Don't have an account?
          <Link to={`/${ROUTE_SOURCES.REGISTER}`}> Sign Up</Link>
        </Typography>
        <Footer />
      </>
    </Modal>
  );
};
